import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, Dropdown, TextInput } from "flowbite-react";
import {
  HiOutlineChevronDown,
  HiOutlinePlus,
  HiOutlineTrash,
  HiSearch,
} from "react-icons/hi";
import { Parameter } from "../../models";
import { Button } from "../Button/Button";
import { EmptyState } from "../EmptyState/EmptyState";
import { CategoryGridSkeleton } from "../Skeleton/CategoryGridSkeleton";
import { ConfirmDeleteModal } from "../Dialog/ConfirmDeleteModal";
import { Stack, Inline } from "@design-system";
import { ToggleSwitch } from "../ToggleSwitch/ToggleSwitch";
import type { TranslateFn } from "../../types";

const selectedBadgeClass =
  "bg-pickup-muted text-pickup";
const unselectedBadgeClass =
  "bg-surface-muted text-secondary";

interface CategoryTabProps {
  label: string;
  count: number;
  isSelected: boolean;
  onClick: () => void;
}

const CategoryTab = ({ label, count, isSelected, onClick }: CategoryTabProps) => (
  <li className="mr-2 relative">
    <div className="flex items-center">
      <button
        className={`inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg ${
          isSelected
            ? "text-brand border-brand bg-brand-subtle font-semibold"
            : "border-transparent text-muted hover:text-secondary hover:border-border-strong hover:bg-surface-hover"
        }`}
        onClick={onClick}
        aria-current={isSelected ? "page" : undefined}
      >
        <span>{label}</span>
        <Badge
          className={`ml-2 ${isSelected ? selectedBadgeClass : unselectedBadgeClass}`}
        >
          {count}
        </Badge>
      </button>
    </div>
  </li>
);

interface MobileCategoryDropdownProps {
  categories: Parameter[];
  itemsByCategory: Record<string, unknown[]>;
  selectedCategoryId: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  allItemsCount: number;
  allLabel: string;
  uncategorizedLabel: string;
  addCategoryLabel: string;
  showEmptyCategories: boolean;
  onAddCategory?: () => void;
}

const MobileCategoryDropdown = ({
  categories,
  itemsByCategory,
  selectedCategoryId,
  onSelectCategory,
  allItemsCount,
  allLabel,
  uncategorizedLabel,
  addCategoryLabel,
  showEmptyCategories,
  onAddCategory,
}: MobileCategoryDropdownProps) => {
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.code.localeCompare(b.code)),
    [categories],
  );

  const currentCategoryLabel = useMemo(() => {
    if (selectedCategoryId === null) {
      return allLabel;
    } else if (selectedCategoryId === "uncategorized") {
      return uncategorizedLabel;
    } else {
      const category = categories.find((c) => c.id === selectedCategoryId);
      return category ? category.code : allLabel;
    }
  }, [selectedCategoryId, categories, allLabel, uncategorizedLabel]);

  return (
    <div className="w-full mb-4">
      <Stack gap={2}>
        <Inline gap={2}>
          <Dropdown
            label={
              <div className="flex items-center justify-between w-full">
                <span>{currentCategoryLabel}</span>
                <Badge
                  className={`ml-2 ${selectedCategoryId ? selectedBadgeClass : unselectedBadgeClass}`}
                >
                  {selectedCategoryId === null
                    ? allItemsCount
                    : itemsByCategory[selectedCategoryId]?.length || 0}
                </Badge>
                <HiOutlineChevronDown className="ml-2 h-5 w-5" />
              </div>
            }
            color="light"
            className="w-full"
          >
            <Dropdown.Item onClick={() => onSelectCategory(null)}>
              <div className="flex items-center justify-between w-full">
                <span>{allLabel}</span>
                <Badge
                  className={
                    selectedCategoryId === null
                      ? selectedBadgeClass
                      : unselectedBadgeClass
                  }
                >
                  {allItemsCount}
                </Badge>
              </div>
            </Dropdown.Item>

            {sortedCategories.map((category) => {
              const count = itemsByCategory[category.id]?.length || 0;
              if (count === 0 && !showEmptyCategories) return null;

              return (
                <Dropdown.Item
                  key={category.id}
                  onClick={() => onSelectCategory(category.id)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{category.code}</span>
                    <Badge
                      className={
                        selectedCategoryId === category.id
                          ? selectedBadgeClass
                          : unselectedBadgeClass
                      }
                    >
                      {count}
                    </Badge>
                  </div>
                </Dropdown.Item>
              );
            })}

            {itemsByCategory["uncategorized"] &&
              itemsByCategory["uncategorized"].length > 0 && (
                <Dropdown.Item onClick={() => onSelectCategory("uncategorized")}>
                  <div className="flex items-center justify-between w-full">
                    <span>{uncategorizedLabel}</span>
                    <Badge
                      className={
                        selectedCategoryId === "uncategorized"
                          ? selectedBadgeClass
                          : unselectedBadgeClass
                      }
                    >
                      {itemsByCategory["uncategorized"].length}
                    </Badge>
                  </div>
                </Dropdown.Item>
              )}
          </Dropdown>

          {onAddCategory && (
            <Button
              color="light"
              size="sm"
              onClick={onAddCategory}
              className="flex-shrink-0"
              title={addCategoryLabel}
            >
              <HiOutlinePlus className="h-5 w-5" />
            </Button>
          )}
        </Inline>
      </Stack>
    </div>
  );
};

export interface CatalogCategoryGridLabels {
  /** Translated "All vehicles"/"All products" label. */
  allLabel: string;
  /** Translated empty-state shown when there are no items at all. */
  noItems: { title: string; description: string; buttonText: string };
}

export interface CatalogCategoryGridProps<T> {
  items: T[];
  categories: Parameter[];
  isLoading: boolean;
  selectedCategoryId: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onSelectItem: (item: T) => void;
  onAddCategory?: () => void;
  onDeleteCategory?: (categoryId: string) => void;
  /** Translation function bound to the item namespace (e.g. "vehicles"/"products"). */
  t: TranslateFn;
  /** i18n namespace, e.g. "vehicles" or "products". */
  namespace: string;
  /** Asymmetric labels that differ between namespaces. */
  labels: CatalogCategoryGridLabels;
  /** DOM id for the search input. */
  searchInputId: string;
  /** Illustration for the "no items at all" empty state. */
  emptyIllustration: ReactNode;
  /** Maps an item to a known category id, or null when uncategorized. */
  getCategoryIdForItem: (item: T, categories: Parameter[]) => string | null;
  /** Search predicate against a lower-cased search term. */
  matchesSearch: (item: T, lowerSearch: string) => boolean;
  /** Renders a single item card. Must set a stable `key` on the returned element. */
  renderCard: (item: T) => ReactNode;
  /** Builds a default item, optionally pre-filled with the selected category. */
  createDefaultItem: (selectedCategoryId: string | null) => T;
  /** Whether the search box is shown even when no items match (products: true). */
  alwaysShowSearch: boolean;
  /** Whether to render a dedicated "no search results" empty state (products: true). */
  showNoSearchResults: boolean;
}

/**
 * Shared catalog category browser (tabs/mobile dropdown + search + grid) used by
 * the Vehicles and Products inventory pages. Item-specific rendering and grouping
 * are supplied through props so the shell stays behavior-identical for both.
 */
export const CatalogCategoryGrid = <T,>({
  items,
  categories,
  isLoading,
  selectedCategoryId,
  onSelectCategory,
  onSelectItem,
  onAddCategory,
  onDeleteCategory,
  t,
  namespace,
  labels,
  searchInputId,
  emptyIllustration,
  getCategoryIdForItem,
  matchesSearch,
  renderCard,
  createDefaultItem,
  alwaysShowSearch,
  showNoSearchResults,
}: CatalogCategoryGridProps<T>) => {
  const [itemsByCategory, setItemsByCategory] = useState<Record<string, T[]>>(
    {},
  );
  const [allItems, setAllItems] = useState<T[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [showEmptyCategories, setShowEmptyCategories] =
    useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const uncategorizedLabel = t(`${namespace}:categories.uncategorized`);

  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Group items by category
  useEffect(() => {
    if (!isLoading && items.length > 0) {
      const grouped: Record<string, T[]> = {};
      const uncategorized: T[] = [];

      categories.forEach((category) => {
        grouped[category.id] = [];
      });

      items.forEach((item) => {
        const categoryId = getCategoryIdForItem(item, categories);
        if (categoryId !== null && grouped[categoryId]) {
          grouped[categoryId].push(item);
        } else {
          uncategorized.push(item);
        }
      });

      if (uncategorized.length > 0) {
        grouped["uncategorized"] = uncategorized;
      }

      setItemsByCategory(grouped);
      setAllItems(items);
    } else if (!isLoading && items.length === 0) {
      setItemsByCategory({});
      setAllItems([]);
    }
  }, [items, categories, isLoading, getCategoryIdForItem]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.code.localeCompare(b.code)),
    [categories],
  );

  const filteredItems = useMemo(() => {
    let result =
      selectedCategoryId === null
        ? allItems
        : selectedCategoryId === "uncategorized"
          ? itemsByCategory["uncategorized"] || []
          : itemsByCategory[selectedCategoryId] || [];

    if (debouncedSearchTerm.trim() !== "") {
      const searchLower = debouncedSearchTerm.toLowerCase();
      result = result.filter((item) => matchesSearch(item, searchLower));
    }

    return result;
  }, [
    selectedCategoryId,
    allItems,
    itemsByCategory,
    debouncedSearchTerm,
    matchesSearch,
  ]);

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategoryId || selectedCategoryId === "uncategorized")
      return "";
    const category = categories.find((c) => c.id === selectedCategoryId);
    return category ? category.code : "";
  }, [selectedCategoryId, categories]);

  const canDeleteSelectedCategory = useMemo(() => {
    return (
      selectedCategoryId !== null &&
      selectedCategoryId !== "uncategorized" &&
      selectedCategoryId !== "all"
    );
  }, [selectedCategoryId]);

  const handleDeleteCategory = () => {
    if (canDeleteSelectedCategory && onDeleteCategory) {
      onDeleteCategory(selectedCategoryId!);
      setShowDeleteModal(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const onToggleEmptyCategories = useCallback(() => {
    const newShowEmptyValue = !showEmptyCategories;
    setShowEmptyCategories(newShowEmptyValue);

    // Hvis vi slår av visning av tomme kategorier og den valgte kategorien er tom,
    // bytter vi tilbake til "alle kategorier"
    if (
      !newShowEmptyValue &&
      selectedCategoryId !== null &&
      selectedCategoryId !== "all"
    ) {
      const categoryItems = itemsByCategory[selectedCategoryId]?.length || 0;
      if (categoryItems === 0) {
        onSelectCategory(null);
      }
    }
  }, [showEmptyCategories, selectedCategoryId, itemsByCategory, onSelectCategory]);

  if (isLoading) {
    return <CategoryGridSkeleton />;
  }

  if (allItems.length === 0) {
    return (
      <EmptyState
        icon={emptyIllustration}
        title={labels.noItems.title}
        description={labels.noItems.description}
        buttonText={labels.noItems.buttonText}
        buttonClick={() => onSelectItem(createDefaultItem(null))}
      />
    );
  }

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  const emptyCategoryPlaceholder = (
    <EmptyState
      icon={
        <div className="mx-auto w-16 h-16 text-purple-600 mb-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
        </div>
      }
      title={t(`${namespace}:categories.empty_category.title`)}
      description={
        selectedCategory
          ? t(`${namespace}:categories.empty_category.description`, {
              category: selectedCategory.code,
            })
          : t(`${namespace}:categories.empty_category.description_generic`)
      }
      buttonText={t(`${namespace}:categories.empty_category.button_text`)}
      buttonClick={() => onSelectItem(createDefaultItem(selectedCategoryId))}
    />
  );

  return (
    <div className="space-y-6">
      {/* Categories Navigation - Tabs for desktop, Dropdown for mobile */}
      {isMobile ? (
        <MobileCategoryDropdown
          categories={categories}
          itemsByCategory={itemsByCategory}
          selectedCategoryId={selectedCategoryId}
          onSelectCategory={onSelectCategory}
          allItemsCount={allItems.length}
          allLabel={labels.allLabel}
          uncategorizedLabel={uncategorizedLabel}
          addCategoryLabel={t(`${namespace}:categories.add_category`)}
          showEmptyCategories={showEmptyCategories}
          onAddCategory={onAddCategory}
        />
      ) : (
        <Stack gap={2}>
          <div className="flex items-center border-b border-border overflow-x-auto pb-1">
            <ul className="flex flex-nowrap -mb-px text-sm font-medium text-center min-w-max">
              <CategoryTab
                label={labels.allLabel}
                count={allItems.length}
                isSelected={selectedCategoryId === null}
                onClick={() => onSelectCategory(null)}
              />

              {sortedCategories.map((category) => {
                const count = itemsByCategory[category.id]?.length || 0;
                if (count === 0 && !showEmptyCategories) return null;

                return (
                  <CategoryTab
                    key={category.id}
                    label={category.code}
                    count={count}
                    isSelected={selectedCategoryId === category.id}
                    onClick={() => onSelectCategory(category.id)}
                  />
                );
              })}

              {itemsByCategory["uncategorized"] &&
                itemsByCategory["uncategorized"].length > 0 && (
                  <CategoryTab
                    label={uncategorizedLabel}
                    count={itemsByCategory["uncategorized"].length}
                    isSelected={selectedCategoryId === "uncategorized"}
                    onClick={() => onSelectCategory("uncategorized")}
                  />
                )}
            </ul>
          </div>
        </Stack>
      )}

      {/* Search bar and action buttons */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        {(alwaysShowSearch || filteredItems.length > 0) && (
          <div className="w-full sm:w-1/2">
            <TextInput
              id={searchInputId}
              type="text"
              icon={HiSearch}
              placeholder={t("common:search")}
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full"
              aria-label={t("common:search")}
            />
          </div>
        )}
        <div
          className={`flex items-center gap-2 ${filteredItems.length === 0 ? "ml-auto" : ""}`}
        >
          <div className="flex items-center gap-2 mr-2">
            <span className="text-sm text-muted">
              {t(`${namespace}:categories.show_empty`)}
            </span>
            <ToggleSwitch
              as="div"
              checked={showEmptyCategories}
              onChange={onToggleEmptyCategories}
              trackModifierClassName="cursor-pointer"
              tabIndex={0}
              ariaLabel={t(`${namespace}:categories.show_empty`)}
            />
          </div>

          {onAddCategory && (
            <Button
              color="light"
              size="sm"
              onClick={onAddCategory}
              title={t(`${namespace}:categories.add_category`)}
              aria-label={t(`${namespace}:categories.add_category`)}
            >
              <HiOutlinePlus className="h-5 w-5" />
            </Button>
          )}

          {onDeleteCategory && (
            <Button
              color="light"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
              title={t(`${namespace}:categories.delete_category`)}
              disabled={!canDeleteSelectedCategory}
              aria-label={t(`${namespace}:categories.delete_category`)}
            >
              <HiOutlineTrash className="h-5 w-5 text-red-600 dark:text-red-400" />
            </Button>
          )}
        </div>
      </div>

      {/* Delete Category Modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteCategory}
        title={t(`${namespace}:categories.delete_modal.title`)}
        description={t(`${namespace}:categories.delete_modal.description`, {
          category: selectedCategoryName,
        })}
        highlightedName={selectedCategoryName}
        cancelLabel={t("common:cancel")}
        confirmLabel={t("common:delete")}
      />

      {/* Items Grid - Adjust columns based on screen size */}
      <div className="mt-4">
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => renderCard(item))}
          </div>
        ) : showNoSearchResults && debouncedSearchTerm.trim() !== "" ? (
          <EmptyState
            icon={
              <div className="mx-auto w-16 h-16 text-purple-600 mb-2">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            }
            title={t(`${namespace}:search.no_results`)}
            description={t("common:no_results_for_search", {
              search: debouncedSearchTerm,
            })}
            buttonText={t("common:clear_search")}
            buttonClick={() => {
              setSearchTerm("");
              setDebouncedSearchTerm("");
            }}
            secondaryButtonText={t(
              `${namespace}:categories.empty_category.button_text`,
            )}
            secondaryButtonClick={() => {
              setSearchTerm("");
              setDebouncedSearchTerm("");
              onSelectItem(createDefaultItem(null));
            }}
          />
        ) : (
          selectedCategoryId !== null && emptyCategoryPlaceholder
        )}
      </div>
    </div>
  );
};
